import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import UserAvatar from '../../common/UserAvatar';
import {
  Mail, Phone, Calendar as CalendarIcon, Wallet, Star, MapPin, Shield,
  CheckCircle, AlertTriangle, Briefcase
} from 'lucide-react';
import { getRoleBadge } from './roleUtils';

export default function UserProfileModal({ open, onOpenChange, profile, loading, backendUrl, onAdjustWallet }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-[Poppins]">User Profile Details</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
          </div>
        ) : profile && (
          <div className="space-y-6 mt-4">
            {/* Basic Info */}
            <div className="flex items-start gap-4 p-4 bg-stone-50 rounded-xl">
              <UserAvatar src={profile.profile_image} name={profile.name} role={profile.role} size="xl" />
              <div className="flex-1">
                <h3 className="text-lg font-bold text-slate-900">{profile.name}</h3>
                <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-1 ${getRoleBadge(profile.role)}`}>
                  {profile.role}
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3 text-sm">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Mail className="w-4 h-4 text-slate-400" />
                    {profile.email || '-'}
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <Phone className="w-4 h-4 text-slate-400" />
                    {profile.phone || '-'}
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <CalendarIcon className="w-4 h-4 text-slate-400" />
                    Joined {profile.created_at ? new Date(profile.created_at).toLocaleDateString() : '-'}
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <span className={`w-2 h-2 rounded-full ${profile.is_active ? 'bg-green-500' : 'bg-red-500'}`}></span>
                    {profile.is_active ? 'Active' : 'Inactive'}
                  </div>
                </div>
              </div>
            </div>

            {/* Stats (customers) */}
            {profile.role === 'customer' && (
              <div className="grid grid-cols-3 gap-4">
                <div
                  className="p-4 bg-blue-50 rounded-xl text-center cursor-pointer hover:bg-blue-100 transition-colors"
                  onClick={() => onAdjustWallet(profile.id, profile.name)}
                >
                  <Wallet className="w-6 h-6 text-blue-600 mx-auto mb-1" />
                  <p className="text-xl font-bold text-blue-700">₱{(profile.wallet_balance || 0).toLocaleString()}</p>
                  <p className="text-xs text-blue-600">Wallet Balance</p>
                  <p className="text-xs text-blue-400 mt-1">Click to adjust</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-xl text-center">
                  <CalendarIcon className="w-6 h-6 text-amber-600 mx-auto mb-1" />
                  <p className="text-xl font-bold text-amber-700">{profile.total_bookings || 0}</p>
                  <p className="text-xs text-amber-600">Total Bookings</p>
                </div>
                <div className="p-4 bg-green-50 rounded-xl text-center">
                  <Star className="w-6 h-6 text-green-600 mx-auto mb-1" />
                  <p className="text-xl font-bold text-green-700">{profile.total_spent?.toLocaleString() || 0}</p>
                  <p className="text-xs text-green-600">Total Spent (₱)</p>
                </div>
              </div>
            )}

            {/* Stats (providers) */}
            {profile.role === 'provider' && (
              <div className="grid grid-cols-3 gap-4">
                <div
                  className="p-4 bg-blue-50 rounded-xl text-center cursor-pointer hover:bg-blue-100 transition-colors"
                  onClick={() => onAdjustWallet(profile.id, profile.name)}
                >
                  <Wallet className="w-6 h-6 text-blue-600 mx-auto mb-1" />
                  <p className="text-xl font-bold text-blue-700">₱{(profile.wallet_balance || 0).toLocaleString()}</p>
                  <p className="text-xs text-blue-600">Wallet Balance</p>
                  <p className="text-xs text-blue-400 mt-1">Click to adjust</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-xl text-center">
                  <CalendarIcon className="w-6 h-6 text-amber-600 mx-auto mb-1" />
                  <p className="text-xl font-bold text-amber-700">{profile.provider_profile?.total_jobs || 0}</p>
                  <p className="text-xs text-amber-600">Total Jobs</p>
                </div>
                <div className="p-4 bg-green-50 rounded-xl text-center">
                  <Star className="w-6 h-6 text-green-600 mx-auto mb-1" />
                  <p className="text-xl font-bold text-green-700">{profile.provider_profile?.average_rating?.toFixed(1) || '0.0'}</p>
                  <p className="text-xs text-green-600">Rating</p>
                </div>
              </div>
            )}

            {/* Addresses */}
            <div className="border rounded-xl p-4">
              <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-slate-500" />
                Saved Addresses
              </h4>
              {profile.addresses?.length > 0 ? (
                <div className="space-y-3">
                  {profile.addresses.map((addr, idx) => (
                    <div key={addr.id || `addr-${addr.label || idx}-${(addr.full_address || '').substring(0, 20)}`} className="p-3 bg-stone-50 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-slate-900">{addr.label || `Address ${idx + 1}`}</p>
                          <p className="text-sm text-slate-600 mt-1">{addr.full_address || addr.address}</p>
                          {(addr.barangay || addr.city || addr.province) && (
                            <p className="text-sm text-slate-500">
                              {[addr.barangay, addr.city, addr.province].filter(Boolean).join(', ')}
                            </p>
                          )}
                          {addr.landmark && (
                            <p className="text-xs text-slate-400 mt-1">Landmark: {addr.landmark}</p>
                          )}
                        </div>
                        {addr.is_default && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">Default</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-4">No addresses saved</p>
              )}
            </div>

            {/* Identity Verification */}
            <div className="border rounded-xl p-4">
              <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4 text-slate-500" />
                Identity Verification
              </h4>

              <div className="flex items-center gap-3 mb-4 p-3 rounded-lg bg-stone-50">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${profile.identity_verified || profile.face_verified ? 'bg-green-100' : 'bg-amber-100'}`}>
                  {profile.identity_verified || profile.face_verified ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  )}
                </div>
                <div>
                  <p className={`font-medium ${profile.identity_verified || profile.face_verified ? 'text-green-700' : 'text-amber-700'}`}>
                    {profile.identity_verified || profile.face_verified ? 'Verified' : 'Not Verified'}
                  </p>
                  {(profile.verification_date || profile.face_verification_date) && (
                    <p className="text-xs text-slate-500">
                      Verified on {new Date(profile.verification_date || profile.face_verification_date).toLocaleDateString()}
                    </p>
                  )}
                  {profile.verification_method && (
                    <p className="text-xs text-slate-500">
                      Method: {profile.verification_method === 'name_ocr' ? 'Name OCR' : profile.verification_method}
                    </p>
                  )}
                </div>
              </div>

              {(profile.verified_id_image_url || profile.verification_documents?.length > 0 || profile.latest_verification?.id_image_url) && (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-slate-700">Submitted ID Document:</p>

                  {(profile.verified_id_image_url || profile.latest_verification?.id_image_url) ? (
                    <div className="relative">
                      <img
                        src={`${backendUrl}${profile.verified_id_image_url || profile.latest_verification?.id_image_url}`}
                        alt="Verified ID"
                        className="w-full max-w-md rounded-lg border border-slate-200 shadow-sm"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <div className="hidden p-4 bg-slate-100 rounded-lg text-center text-sm text-slate-500">
                        ID image could not be loaded
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-xs px-2 py-1 bg-slate-100 rounded text-slate-600">
                          Type: {profile.verified_id_type || 'Government ID'}
                        </span>
                        {(profile.name_verification_score || profile.face_similarity_score) ? (
                          <span className="text-xs px-2 py-1 bg-green-100 rounded text-green-700">
                            {profile.verification_method === 'face'
                              ? `Face Match: ${(profile.face_similarity_score || 0).toFixed(0)}%`
                              : `Name Match: ${(profile.name_verification_score || 0).toFixed(0)}%`}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {profile.verification_documents?.filter(doc => doc.document_type !== 'selfie').map((doc, idx) => (
                        <div key={doc.id || `doc-${doc.document_type}-${idx}`} className="border rounded-lg overflow-hidden">
                          {doc.file_url ? (
                            <img
                              src={`${backendUrl}${doc.file_url}`}
                              alt={`ID Document ${idx + 1}`}
                              className="w-full h-40 object-cover"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <div className="w-full h-40 bg-slate-100 flex items-center justify-center text-slate-400">
                              No preview available
                            </div>
                          )}
                          <div className="p-2 bg-slate-50">
                            <p className="text-xs font-medium text-slate-700 capitalize">
                              {doc.document_type?.replace('_', ' ') || 'ID Document'}
                            </p>
                            <p className="text-xs text-slate-500">
                              Status: <span className={doc.status === 'verified' ? 'text-green-600' : 'text-amber-600'}>{doc.status}</span>
                            </p>
                            {doc.uploaded_at && (
                              <p className="text-xs text-slate-400">
                                Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {!profile.verified_id_image_url && !profile.latest_verification?.id_image_url && (!profile.verification_documents || profile.verification_documents.length === 0) && (
                <p className="text-sm text-slate-400 text-center py-4">No ID documents submitted</p>
              )}
            </div>

            {/* Provider-specific info */}
            {profile.role === 'provider' && profile.provider_profile && (
              <div className="border rounded-xl p-4">
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-slate-500" />
                  Provider Profile
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="p-3 bg-amber-50 rounded-lg text-center">
                    <Star className="w-5 h-5 text-amber-500 mx-auto mb-1" />
                    <p className="text-lg font-bold text-amber-700">{profile.provider_profile.rating?.toFixed(1) || '0.0'}</p>
                    <p className="text-xs text-amber-600">Rating</p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg text-center">
                    <Briefcase className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                    <p className="text-lg font-bold text-blue-700">{profile.provider_profile.total_jobs || 0}</p>
                    <p className="text-xs text-blue-600">Jobs</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg text-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mx-auto mb-1" />
                    <p className="text-lg font-bold text-green-700">{profile.provider_profile.completion_rate || 0}%</p>
                    <p className="text-xs text-green-600">Completion</p>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg text-center">
                    <Wallet className="w-5 h-5 text-purple-500 mx-auto mb-1" />
                    <p className="text-lg font-bold text-purple-700">₱{(profile.provider_profile.earnings || 0).toLocaleString()}</p>
                    <p className="text-xs text-purple-600">Earnings</p>
                  </div>
                </div>
                {profile.provider_profile.services?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm text-slate-500 mb-2">Services:</p>
                    <div className="flex flex-wrap gap-2">
                      {profile.provider_profile.services.map((s) => (
                        <span key={`service-${s}`} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-lg">{s}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
